import round6Decimals from "../Components/round6Decimals";

/**
 * Descuento predeterminado de cliente/proveedor + descuento adicional por
 * operacion (empresas con el complemento Escuela).
 *
 * El renglon guarda tres campos:
 *   - descuentoBase: el % predeterminado del cliente (ventas) o del proveedor
 *     (compras). Se precarga y se puede mover a mano.
 *   - descuentoAdicional: el % extra de esa operacion.
 *   - descuento: la combinacion EN CASCADA de los dos.
 *
 * `descuento` sigue siendo el unico que entra en los calculos: el backend
 * (CFDI, polizas, costo de inventario, validacion de la O/C), los PDFs y los
 * reportes solo leen ese campo. Los otros dos son informativos, para pintar las
 * dos columnas y para que sobrevivan al pasar de un documento a otro.
 */

const porcentaje = (valor) => {
  const numero = Number(valor);
  if (!Number.isFinite(numero) || numero <= 0) return 0;
  if (numero >= 100) return 100;
  return numero;
};

// 10% y luego 5% sobre lo que queda = 14.5%
export const combinarDescuentos = (base, adicional) => {
  const b = porcentaje(base);
  const a = porcentaje(adicional);
  return round6Decimals(100 * (1 - (1 - b / 100) * (1 - a / 100)));
};

/**
 * Parte un descuento efectivo (p. ej. el de un XML) en base + adicional,
 * conservando la base cuando cabe. Si el efectivo es menor que la base, la
 * base baja al efectivo y el adicional queda en 0.
 */
export const descomponerDescuento = (efectivo, base) => {
  const e = porcentaje(efectivo);
  const b = porcentaje(base);
  if (e <= b) return { descuentoBase: e, descuentoAdicional: 0 };
  if (b >= 100) return { descuentoBase: 100, descuentoAdicional: 0 };
  return {
    descuentoBase: b,
    descuentoAdicional: round6Decimals(100 * (1 - (1 - e / 100) / (1 - b / 100))),
  };
};

/**
 * Renglon guardado antes de esta funcionalidad (o que llego sin desglose): todo
 * su descuento se toma como adicional, asi el efectivo no cambia.
 *
 * "Sin desglose" incluye base y adicional en 0: es lo que dejan los initialValue
 * de las columnas al montarse sobre un renglon viejo. Un renglon que el usuario
 * dejo en 0 y 0 ya tiene `descuento` en 0 (se sincroniza en cada edicion), asi
 * que tomarlo como adicional no le cambia nada.
 * Muta la fila a proposito, igual que los handlers de las pantallas.
 */
export const normalizarDescuentoFila = (fila) => {
  if (!fila) return fila;
  if (porcentaje(fila.descuentoBase) > 0 || porcentaje(fila.descuentoAdicional) > 0) return fila;
  fila.descuentoBase = 0;
  fila.descuentoAdicional = porcentaje(fila.descuento);
  return fila;
};

/**
 * Recalcula `descuento` desde las dos columnas. Solo para cuando el usuario
 * acaba de mover alguna: NO normaliza, porque ahi `descuento` todavia trae el
 * valor anterior y normalizar reviviria un descuento que se acaba de borrar.
 */
export const sincronizarDescuentoFila = (fila) => {
  if (!fila) return fila;
  fila.descuento = combinarDescuentos(fila.descuentoBase, fila.descuentoAdicional);
  return fila;
};

// Pone el % predeterminado como base y conserva el adicional que ya traia.
// Normaliza antes: aqui `descuento` si esta al dia (o es el de un renglon viejo).
export const aplicarDescuentoBaseAFila = (fila, porcentajeDefault) => {
  if (!fila) return fila;
  normalizarDescuentoFila(fila);
  fila.descuentoBase = porcentaje(porcentajeDefault);
  return sincronizarDescuentoFila(fila);
};

/**
 * Precio a precargar en compras cuando el renglon lleva descuento del
 * proveedor. El costo de la relacion y del insumo queda NETO del descuento de
 * la ultima compra (cambiarCostosInsumos guarda el bruto en costoSinDescuento);
 * precargar el neto y encima el % del proveedor lo descontaria dos veces.
 * Un costoSinDescuento menor que el costo esta viejo y no se usa.
 */
export const costoParaDescuentoProveedor = (insumo) => {
  const costo = Number(insumo?.costo) || 0;
  const sinDescuento = Number(insumo?.costoSinDescuento);
  if (Number.isFinite(sinDescuento) && sinDescuento >= costo) return sinDescuento;
  return costo;
};
